import React from 'react';
import { motion } from 'framer-motion';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell
} from 'recharts';
import { PieChart as PieChartIcon, BarChart2, AlertCircle } from 'lucide-react';

interface AnalyticsChartsProps {
    revenueData: any[];
    servicesData: any[];
    totalAppointments: number;
}

const COLORS = {
    primary: '#D96C4F',
    charts: { grid: '#E5E7EB' },
    text: { light: '#9CA3AF' }
};

const AnalyticsCharts: React.FC<AnalyticsChartsProps> = ({ revenueData, servicesData, totalAppointments }) => {

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: { y: 0, opacity: 1 }
    };

    // Componente Empty State Riutilizzabile interno
    const EmptyState = ({ icon: Icon, title, desc }: { icon: any, title: string, desc: string }) => (
        <div className="h-full w-full flex flex-col items-center justify-center text-center p-6 animate-in fade-in zoom-in-95">
            <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4 border border-gray-100">
                <Icon size={24} className="text-gray-300" />
            </div>
            <h4 className="text-sm font-bold text-gray-900">{title}</h4>
            <p className="text-xs text-gray-400 mt-1 max-w-[220px] leading-relaxed">{desc}</p>
        </div>
    );

    return (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

            {/* Revenue Area Chart */}
            <motion.div variants={itemVariants} className="lg:col-span-8 bg-white rounded-2xl border border-gray-100 shadow-sm p-6 md:p-8">
                <div className="flex justify-between items-center mb-8">
                    <div>
                        <h3 className="text-xl font-bold text-gray-900">Andamento Fatturato</h3>
                        <p className="text-sm text-gray-400">Entrate totali nel periodo selezionato</p>
                    </div>
                </div>
                <div className="h-80 w-full">
                    {revenueData.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={revenueData}>
                                <defs>
                                    <linearGradient id="colorRevenueMain" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.3} />
                                        <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={COLORS.charts.grid} />
                                <XAxis
                                    dataKey="name"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: COLORS.text.light, fontSize: 12 }}
                                    dy={10}
                                />
                                <YAxis
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: COLORS.text.light, fontSize: 12 }}
                                    tickFormatter={(value) => `€${value}`}
                                />
                                <Tooltip
                                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }}
                                    cursor={{ stroke: COLORS.primary, strokeWidth: 1 }}
                                    formatter={(value: number) => [`€ ${value}`, 'Fatturato']}
                                />
                                <Area
                                    type="monotone"
                                    dataKey="value"
                                    stroke={COLORS.primary}
                                    strokeWidth={3}
                                    fillOpacity={1}
                                    fill="url(#colorRevenueMain)"
                                    activeDot={{ r: 6, strokeWidth: 0 }}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    ) : (
                        <EmptyState
                            icon={BarChart2}
                            title="Nessun dato di fatturato"
                            desc="Non ci sono movimenti finanziari registrati per questo periodo."
                        />
                    )}
                </div>
            </motion.div>

            {/* Services Donut Chart */}
            <motion.div variants={itemVariants} className="lg:col-span-4 bg-white rounded-2xl border border-gray-100 shadow-sm p-6 md:p-8 flex flex-col">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Mix Servizi</h3>
                <p className="text-sm text-gray-400 mb-4">Distribuzione % dei servizi</p>

                {servicesData.length > 0 ? (
                    <>
                        <div className="h-64 relative flex-shrink-0">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={servicesData}
                                        innerRadius={60}
                                        outerRadius={80}
                                        paddingAngle={5}
                                        dataKey="value"
                                        stroke="none"
                                    >
                                        {servicesData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                        formatter={(value, name) => [`${value}%`, name]}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                                <span className="text-3xl font-bold text-gray-900">{totalAppointments}</span>
                                <span className="text-[10px] text-gray-400 uppercase font-bold tracking-wider">Bookings</span>
                            </div>
                        </div>

                        <div className="mt-6 space-y-3 overflow-y-auto pr-2 max-h-[150px] custom-scrollbar">
                            {servicesData.map((service, i) => (
                                <div key={i} className="flex items-center justify-between text-sm">
                                    <div className="flex items-center gap-2">
                                        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: service.color }}></div>
                                        <span className="text-gray-600 font-medium truncate max-w-[120px]">{service.name}</span>
                                    </div>
                                    <span className="font-bold text-gray-900">{service.value}%</span>
                                </div>
                            ))}
                        </div>
                    </>
                ) : (
                    <div className="flex-1 flex items-center justify-center">
                        <EmptyState
                            icon={PieChartIcon}
                            title="Nessun servizio"
                            desc="Non ci sono appuntamenti o servizi registrati in questo lasso di tempo."
                        />
                    </div>
                )}
            </motion.div>
        </div>
    );
};

export default AnalyticsCharts;